import type { PoseCelda, TestConfigPayload } from './tipos';

/**
 * Body de generación de una celda del test masivo — contrato adBody REAL de
 * atlas-backend/src/routes/ads.ts. La regla: nunca mandar keys sin valor
 * (el zod es .optional(), no .nullish(), en casi todos los campos), y cada
 * campo viaja SOLO en el modo al que pertenece — igual que la UI real.
 * `buildTestBody.test.ts` fija el contrato por deep-equal.
 */
export function buildTestBody(
  payload: TestConfigPayload,
  varianteId: string,
  executionId: string,
  /** Pose de ESTA celda (la expansión por pose vive en celdas.ts). Null = default. */
  pose: PoseCelda = null,
): Record<string, unknown> {
  if (payload.modo_test === 'automatico') return buildBodyAutomatico(payload, varianteId, executionId);

  const body: Record<string, unknown> = {
    execution_id: executionId,
    modo: 'personalizado',
    modo_utilizado: 'test_masivo',
    variante_id: varianteId,
    mode: 'single',
    cantidad_imagenes: 1,
    formato: payload.formato,
    composicion: payload.composicion,
    modo_pro: true,
    guardar_como_imagen_generada: true,
  };

  if (payload.composicion === 'modelo') {
    if (payload.modelo_mode) body.modelo_mode = payload.modelo_mode;
    if (payload.modelo_mode === 'guardados' && payload.modelo_guardado_id) {
      body.modelo_guardado_id = payload.modelo_guardado_id;
    }
    if (payload.modelo_mode === 'personalizado') {
      for (const k of ['genero', 'edad', 'tipo_cuerpo', 'tono_piel', 'color_pelo', 'largo_pelo', 'desc_modelo'] as const) {
        const v = payload[k];
        if (v) body[k] = v;
      }
    }
    if (pose?.tipo === 'catalogo') body.pose = [pose.id];
    if (pose?.tipo === 'custom') body.poses_custom = [{ id: pose.id, filename: pose.filename }];
  } else if (payload.vista_producto) {
    body.vista_producto = payload.vista_producto;
  }

  body.background_mode = payload.background_mode;
  if (payload.background_mode === 'blanco' || payload.background_mode === 'elegido') {
    if (payload.background_id != null) body.background_id = payload.background_id;
  } else if (payload.background_mode === 'descripcion') {
    if (payload.background_text) body.background_text = payload.background_text;
  } else if (payload.background_mode === 'guardado') {
    if (payload.background_file_name) body.background_file_name = payload.background_file_name;
  }
  if (payload.composicion === 'modelo' && payload.background_mode === 'blanco') {
    if (payload.fondo_blanco_iluminacion) body.fondo_blanco_iluminacion = payload.fondo_blanco_iluminacion;
    if (payload.fondo_blanco_temperatura) body.fondo_blanco_temperatura = payload.fondo_blanco_temperatura;
  }

  if (payload.instruccion_adicional) body.instruccion_adicional = payload.instruccion_adicional;
  if (payload.provider_override) body.provider_override = payload.provider_override;
  return body;
}

/**
 * Body del MODO AUTOMÁTICO — el del tab `design-auto` de InterfazV2 (modo 'rapido',
 * `composicion: 'producto'`, `background_mode: 'ia'`, `image_size` de la UI) más los
 * overrides que solo existen acá: qué modelo escribe los prompts (`director_override`)
 * y qué informe de marca ve (`brand_context_*`).
 *
 * Nada del modo personalizado viaja: el director de arte no mira modelo, pose, vista
 * ni fondo blanco, y mandarlos ensuciaría el dump del body que se lee en el lightbox.
 * Por eso es una función aparte y no un ramaje de ifs sobre el body personalizado —
 * una config que se pasó de personalizada a automática arrastra restos en el payload.
 *
 * `image_size` va fijo en '1K' (RESOLUCION_DEFAULT): el harness no tiene selector de
 * resolución, y '1K' es lo que manda la UI real mientras nadie lo toque.
 */
function buildBodyAutomatico(
  payload: TestConfigPayload,
  varianteId: string,
  executionId: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    execution_id: executionId,
    modo: 'rapido',
    modo_utilizado: 'test_masivo',
    variante_id: varianteId,
    mode: 'single',
    cantidad_imagenes: cantidadAutomatica(payload),
    formato: payload.formato,
    image_size: '1K',
    composicion: 'producto',
    background_mode: 'ia',
    modo_pro: true,
    guardar_como_imagen_generada: true,
  };
  if (payload.instruccion_adicional) body.instruccion_adicional = payload.instruccion_adicional;
  if (payload.provider_override) body.provider_override = payload.provider_override;
  if (payload.director_override) body.director_override = payload.director_override;
  if (payload.brand_context_mode) {
    body.brand_context_mode = payload.brand_context_mode;
    // El texto solo tiene sentido en 'texto' — en los otros modos el backend lo
    // ignoraría, pero dejarlo haría creer que el informe inventado se usó.
    if (payload.brand_context_mode === 'texto' && payload.brand_context_texto) {
      body.brand_context_texto = payload.brand_context_texto;
    }
  }
  return body;
}

/** Cantidad de imágenes de una celda automática: 1..6, como el control +/- de la UI. */
export function cantidadAutomatica(payload: Pick<TestConfigPayload, 'cantidad_imagenes'>): number {
  return Math.max(1, Math.min(6, Math.trunc(payload.cantidad_imagenes ?? 1)));
}
